"use client";

import { useTranslations } from "use-intl";

import { ArrowForwardIcon } from "@/components/appIcons";
import { FallbackImage } from "@/components/fallback-image";
import { FormatIcon } from "@/components/format-icon";
import { Link } from "@/components/link";
import type { DealOffer } from "@/components/marketplace/deal-offers-types";
import { cn } from "@/lib/utils";

function EditionThumbnail({ offer }: { offer: DealOffer }) {
  return (
    <span className="relative size-9 shrink-0 overflow-hidden rounded bg-secondary">
      {offer.imageUrl ? (
        <FallbackImage src={offer.imageUrl} alt="" sizes="36px" objectFit="contain" />
      ) : (
        <span className="flex size-full items-center justify-center text-muted-foreground">
          <FormatIcon format={offer.formatCategory ?? "other"} size={18} />
        </span>
      )}
    </span>
  );
}

const EDITION_ROW_CLASS = "-mx-2 flex min-w-0 items-center gap-2.5 rounded-md px-2 py-1";

function EditionRowContent({
  label,
  offer,
  tone,
}: {
  label: string;
  offer: DealOffer;
  tone: "primary" | "muted";
}) {
  return (
    <>
      <EditionThumbnail offer={offer} />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span
          className={cn(
            "text-[10px] font-bold uppercase tracking-wider",
            tone === "primary" ? "text-primary" : "text-muted-foreground",
          )}
        >
          {label}
        </span>
        <span className="truncate text-sm font-semibold text-foreground" title={offer.title}>
          {offer.title}
        </span>
      </span>
    </>
  );
}

/**
 * Work scope: names the edition the cheapest price belongs to and links to its release page.
 * `label` replaces the default "Offer edition" eyebrow (the hero panel passes its heading).
 */
export function BestDealOfferEditionRow({ label, offer }: { label?: string; offer: DealOffer }) {
  const t = useTranslations("shared.affiliates");
  const eyebrow = label ?? t("offerEditionLabel");
  const tone = label ? "primary" : "muted";
  if (!offer.matchCatalogId) {
    if (!label) return null;
    return (
      <div className={EDITION_ROW_CLASS}>
        <EditionRowContent label={eyebrow} offer={offer} tone={tone} />
      </div>
    );
  }

  return (
    <Link
      to="/release/$id"
      params={{ id: offer.matchCatalogId }}
      className={cn(
        EDITION_ROW_CLASS,
        "group transition-colors hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
      )}
    >
      <EditionRowContent label={eyebrow} offer={offer} tone={tone} />
      <ArrowForwardIcon
        aria-hidden="true"
        className="shrink-0 text-sm text-primary transition-transform group-hover:translate-x-0.5"
      />
    </Link>
  );
}
